// Contractor weekly reports (V0.4.15): what SWI AI reads from a PBS weekly PDF, the week-on-week compare and the
// cross-check against PM C3. Pure functions — shared by the app and the functions, tested in test/weekly-check.mts.

export type WkItem = { ref: string | null; text: string; status: string | null; page: number | null };
export type WkProc = { item: string; status: string; eta: string | null; flag: 'critical' | 'no_eta' | 'waiting' | null; page: number | null };
export type WkBuilding = {
  name: string;
  pages: string | null; // "5–19"
  progress: string | null;
  lookahead: { area: string | null; text: string; status: 'in_progress' | 'complete' | 'starting' | 'planned' | 'on_hold' | null }[];
  variations: WkItem[];
  rfis: WkItem[];
  open_issues: WkItem[];
  procurement: WkProc[];
  claim: string | null;
  inspections: { text: string; date: string | null }[];
  delays: string[];
};
export type WkRegisterRow = { building: string; ref: string | null; text: string; status: string | null; amount_vep: number | null };
export type WeeklyRead = {
  week_no: number | null;
  period_from: string | null;
  period_to: string | null;
  report_date: string | null;
  contractor: string | null;
  buildings: WkBuilding[];
  registers: WkRegisterRow[]; // variation registers at the back of the report
  notes: string | null;
};

const DATE = /^\d{4}-\d{2}-\d{2}$/;
const str = (v: unknown, max = 600): string | null => {
  if (typeof v !== 'string') return null;
  const t = v.replace(/\s+/g, ' ').trim();
  return t ? t.slice(0, max) : null;
};
const day = (v: unknown) => (typeof v === 'string' && DATE.test(v) && !Number.isNaN(Date.parse(v)) ? v : null);
const int = (v: unknown) => (typeof v === 'number' && Number.isInteger(v) && v > 0 && v < 10000 ? v : null);
const num = (v: unknown) => (typeof v === 'number' && Number.isFinite(v) ? Math.round(v * 100) / 100 : null);
const arr = (v: unknown, max: number): Record<string, unknown>[] => (Array.isArray(v) ? v.filter((x) => x && typeof x === 'object').slice(0, max) as Record<string, unknown>[] : []);
const LA = new Set(['in_progress', 'complete', 'starting', 'planned', 'on_hold']);
const FLAG = new Set(['critical', 'no_eta', 'waiting']);

const item = (x: Record<string, unknown>): WkItem | null => {
  const text = str(x.text, 400);
  return text ? { ref: str(x.ref, 40), text, status: str(x.status, 120), page: int(x.page) } : null;
};

/** Clean what SWI AI returned: trimmed text, real dates, bounded lists, nothing invented. */
export function cleanWeeklyRead(raw: Partial<Record<keyof WeeklyRead, unknown>> | null | undefined): WeeklyRead {
  const r = (raw ?? {}) as Record<string, unknown>;
  const buildings: WkBuilding[] = arr(r.buildings, 20)
    .map((b) => ({
      name: str(b.name, 80) ?? '',
      pages: str(b.pages, 20),
      progress: str(b.progress, 2000),
      lookahead: arr(b.lookahead, 60)
        .map((l) => ({ area: str(l.area, 40), text: str(l.text, 300) ?? '', status: (LA.has(String(l.status)) ? l.status : null) as WkBuilding['lookahead'][number]['status'] }))
        .filter((l) => l.text),
      variations: arr(b.variations, 40).map(item).filter(Boolean) as WkItem[],
      rfis: arr(b.rfis, 40).map(item).filter(Boolean) as WkItem[],
      open_issues: arr(b.open_issues, 40).map(item).filter(Boolean) as WkItem[],
      procurement: arr(b.procurement, 60)
        .map((p) => ({ item: str(p.item, 200) ?? '', status: str(p.status, 300) ?? '', eta: str(p.eta, 40), flag: (FLAG.has(String(p.flag)) ? p.flag : null) as WkProc['flag'], page: int(p.page) }))
        .filter((p) => p.item),
      claim: str(b.claim, 400),
      inspections: arr(b.inspections, 20).map((i) => ({ text: str(i.text, 300) ?? '', date: day(i.date) })).filter((i) => i.text),
      delays: (Array.isArray(b.delays) ? b.delays : []).map((d) => str(d, 300)).filter(Boolean).slice(0, 20) as string[],
    }))
    .filter((b) => b.name);
  return {
    week_no: int(r.week_no),
    period_from: day(r.period_from),
    period_to: day(r.period_to),
    report_date: day(r.report_date),
    contractor: str(r.contractor, 120),
    buildings,
    registers: arr(r.registers, 300)
      .map((x) => ({ building: str(x.building, 80) ?? '', ref: str(x.ref, 40), text: str(x.text, 300) ?? '', status: str(x.status, 160), amount_vep: num(x.amount_vep) }))
      .filter((x) => x.text),
    notes: str(r.notes, 2000),
  };
}

// ---------- matching ----------
const STOP = new Set(['the', 'and', 'for', 'with', 'to', 'of', 'in', 'on', 'is', 'are', 'a', 'an', 'at', 'by', 'be', 'this', 'week', 'works', 'work', 'pending', 'from', 'as', 'all']);
export const norm = (s: string | null | undefined) => (s ?? '').toLowerCase().replace(/&/g, ' and ').replace(/[^a-z0-9]+/g, ' ').trim();
const words = (s: string) => new Set(norm(s).split(' ').filter((w) => w.length > 1 && !STOP.has(w)));
export function similar(a: string, b: string): number {
  const A = words(a);
  const B = words(b);
  if (!A.size || !B.size) return 0;
  let n = 0;
  for (const w of A) if (B.has(w)) n++;
  return n / Math.min(A.size, B.size);
}
/** "RFI 120", "VPR 07", "VPR-7" → "rfi120" / "vpr7"; numbers compared without leading zeros. */
export function refKey(s: string | null | undefined): string | null {
  const m = /\b(rfi|vpr|vo|var|si|nopd|eot|ctc|gc|dt)\s*[#._/-]*\s*0*(\d{1,4})\b/i.exec(s ?? '');
  return m ? `${m[1]!.toLowerCase()}${m[2]}` : null;
}
const sameItem = (a: WkItem | { text: string; ref?: string | null }, b: WkItem | { text: string; ref?: string | null }) => {
  const ra = refKey(a.ref) ?? refKey(a.text);
  const rb = refKey(b.ref) ?? refKey(b.text);
  if (ra && rb) return ra === rb;
  return similar(a.text, b.text) >= 0.6;
};
/** Match a building named in the report ("Staff Village", "Managers House") to a PM C3 building. */
export function matchBuilding<T extends { id: string; name: string; short_code?: string | null }>(name: string, list: T[]): T | null {
  const n = norm(name);
  return (
    list.find((b) => norm(b.name) === n) ??
    list.find((b) => b.short_code && norm(b.short_code) === n) ??
    list.find((b) => norm(b.name).includes(n) || n.includes(norm(b.name))) ??
    list.map((b) => ({ b, s: similar(b.name, name) })).filter((x) => x.s >= 0.5).sort((x, y) => y.s - x.s)[0]?.b ??
    null
  );
}

// ---------- week-on-week compare ----------
export type ChangeKind = 'moved' | 'new' | 'repeated' | 'dropped';
export type Change = { key: string; building: string; section: string; kind: ChangeKind; was: string | null; now: string | null; weeks?: number; note: string };

const SECTIONS: { key: 'open_issues' | 'variations' | 'procurement' | 'rfis'; label: string }[] = [
  { key: 'open_issues', label: 'Open issue' },
  { key: 'variations', label: 'Variation' },
  { key: 'rfis', label: 'RFI' },
  { key: 'procurement', label: 'Procurement' },
];
const asItem = (_s: string, x: WkItem | WkProc): WkItem => ('item' in x ? { ref: null, text: `${x.item} — ${x.status}`, status: x.status, page: x.page } : x);
const label = (x: WkItem) => [x.ref, x.text, x.status && !x.text.includes(x.status) ? `(${x.status})` : null].filter(Boolean).join(' ');
const findB = (r: WeeklyRead | null | undefined, name: string) => r?.buildings.find((b) => norm(b.name) === norm(name) || similar(b.name, name) >= 0.8) ?? null;

/** Compare this week's read with last week's (`older` = earlier reads, newest first, for "repeated for N weeks"). */
export function compareWeeks(cur: WeeklyRead, prev: WeeklyRead | null, older: WeeklyRead[] = []): Change[] {
  if (!prev) return [];
  const out: Change[] = [];
  const streak = (bname: string, pick: (b: WkBuilding) => string | null) => {
    let n = 1;
    for (const r of [prev, ...older]) {
      const b = findB(r, bname);
      const now = pick(findB(cur, bname)!);
      if (b && now && norm(pick(b)) === norm(now)) n++;
      else break;
    }
    return n;
  };
  for (const b of cur.buildings) {
    const p = findB(prev, b.name);
    if (!p) continue;
    // progress text word-for-word the same as last week → nothing reported
    if (b.progress && p.progress && norm(b.progress) === norm(p.progress)) {
      const n = streak(b.name, (x) => x.progress);
      out.push({ key: `${b.name}|progress`, building: b.name, section: 'Progress update', kind: 'repeated', was: p.progress, now: b.progress, weeks: n, note: `Same words as last week${n > 2 ? ` — ${n} weeks running` : ''}. Ask PBS for the real status.` });
    }
    if (b.claim && p.claim && norm(b.claim) !== norm(p.claim)) {
      out.push({ key: `${b.name}|claim`, building: b.name, section: 'Client claim', kind: 'moved', was: p.claim, now: b.claim, note: 'Claim status changed — check it against the Claims register.' });
    }
    for (const s of SECTIONS) {
      const now = (b[s.key] as (WkItem | WkProc)[]).map((x) => asItem(s.key, x));
      const was = (p[s.key] as (WkItem | WkProc)[]).map((x) => asItem(s.key, x));
      const used = new Set<number>();
      for (const x of now) {
        const i = was.findIndex((w, k) => !used.has(k) && sameItem(x, w));
        if (i < 0) {
          out.push({ key: `${b.name}|${s.key}|new|${norm(x.text).slice(0, 60)}`, building: b.name, section: s.label, kind: 'new', was: null, now: label(x), note: refKey(x.ref ?? x.text) ? 'First time in a report — check it is in PM C3.' : 'No reference number given — find or create it in PM C3.' });
          continue;
        }
        used.add(i);
        const w = was[i]!;
        if (norm(label(x)) === norm(label(w))) {
          if (s.key === 'open_issues' || s.key === 'procurement') out.push({ key: `${b.name}|${s.key}|rep|${norm(x.text).slice(0, 60)}`, building: b.name, section: s.label, kind: 'repeated', was: label(w), now: 'Same as last week', note: s.key === 'procurement' ? 'Unchanged — watch it against the programme.' : 'Still open with no change — chase it.' });
        } else {
          out.push({ key: `${b.name}|${s.key}|mov|${norm(x.text).slice(0, 60)}`, building: b.name, section: s.label, kind: 'moved', was: label(w), now: label(x), note: 'Changed since last week.' });
        }
      }
      if (s.key === 'open_issues' || s.key === 'variations') {
        was.forEach((w, k) => {
          if (used.has(k)) return;
          // moved somewhere else in the same building (e.g. an open issue now only under procurement) → not dropped
          const elsewhere = [...b.procurement.map((x) => asItem('procurement', x)), ...b.variations, ...b.open_issues, ...b.rfis].some((x) => sameItem(x, w) || similar(x.text, w.text) >= 0.5);
          out.push(
            elsewhere
              ? { key: `${b.name}|${s.key}|left|${norm(w.text).slice(0, 60)}`, building: b.name, section: s.label, kind: 'moved', was: label(w), now: 'Moved off this list', note: 'Left this list but is still mentioned — it may be closed in PM C3.' }
              : { key: `${b.name}|${s.key}|drop|${norm(w.text).slice(0, 60)}`, building: b.name, section: s.label, kind: 'dropped', was: label(w), now: null, note: 'Gone without a closure note — ask PBS whether it was resolved.' },
          );
        });
      }
    }
  }
  return out;
}

// ---------- cross-check with PM C3 ----------
export type PmItem = { id: string; type: string; number: string | null; ref_no: string | null; title: string | null; status: string | null; building_ids: string[] | null; stage?: string | null; swi_approved_at?: string | null; client_approved_at?: string | null };
export type FindingKind = 'not_in_pm' | 'contradicts' | 'ours' | 'matched';
export type Finding = { key: string; building: string; area: string; kind: FindingKind; says: string; pm: string; action: 'create_request' | 'raise_pbs' | 'open_item' | 'follow_up' | null; itemIds: string[]; page: number | null };

const itemLabel = (i: PmItem) => [i.number, i.ref_no, i.title].filter(Boolean).join(' · ');
function findPm(x: WkItem, list: PmItem[]): PmItem | null {
  const k = refKey(x.ref) ?? refKey(x.text);
  if (k) {
    const byRef = list.find((i) => refKey(i.ref_no) === k || refKey(i.title) === k || (i.number && refKey(i.number) === k));
    if (byRef) return byRef;
    // "RFI 120" against a PM C3 RFI whose own ref is just a number
    const n = k.replace(/^[a-z]+/, '');
    const byNum = list.find((i) => norm(i.ref_no).split(' ').includes(n));
    if (byNum) return byNum;
  }
  return list.map((i) => ({ i, s: similar(i.title ?? '', x.text) })).filter((y) => y.s >= 0.6).sort((a, b) => b.s - a.s)[0]?.i ?? null;
}
const PENDING_FUNDING = /pending\s+(funding|client)\s+approval|awaiting\s+approval|submitted/i;
const APPROVED = /\bapproved\b/i;
const ONGOING = /in progress|underway|ongoing|started/i;

/** What the report says against what PM C3 holds. `buildings` = PM C3 buildings (to scope items by building). */
export function crossCheck(r: WeeklyRead, items: PmItem[], buildings: { id: string; name: string; short_code?: string | null }[]): Finding[] {
  const out: Finding[] = [];
  const vars = items.filter((i) => i.type === 'variation' || i.type === 'request');
  const rfis = items.filter((i) => i.type === 'rfi');
  const claims = items.filter((i) => i.type === 'claim');
  for (const b of r.buildings) {
    const pmB = matchBuilding(b.name, buildings);
    const inB = (i: PmItem) => !pmB || !i.building_ids?.length || i.building_ids.includes(pmB.id);
    // variations mentioned → in PM C3?
    const missing: WkItem[] = [];
    for (const v of b.variations) {
      const hit = findPm(v, vars.filter(inB)) ?? findPm(v, vars);
      if (!hit) missing.push(v);
      else if (v.status && /pricing submitted|pending comments|awaiting (swi|our)/i.test(v.status) && !hit.swi_approved_at)
        out.push({ key: `ours|${hit.id}`, building: b.name, area: 'Variations', kind: 'ours', says: label(v), pm: `${itemLabel(hit)} — waiting at SWI review`, action: 'open_item', itemIds: [hit.id], page: v.page });
      else out.push({ key: `ok|${hit.id}`, building: b.name, area: 'Variations', kind: 'matched', says: label(v), pm: itemLabel(hit), action: null, itemIds: [hit.id], page: v.page });
    }
    if (missing.length)
      out.push({ key: `miss|var|${norm(b.name)}|${missing.map((m) => norm(m.text).slice(0, 20)).join('|')}`, building: b.name, area: 'Variations', kind: 'not_in_pm', says: `${missing.length} variation${missing.length === 1 ? '' : 's'}: ${missing.map(label).join(' · ')}`, pm: `Not in Variations & Requests.`, action: 'create_request', itemIds: [], page: missing[0]!.page });
    // the report's own register at the back vs the building page
    for (const reg of r.registers.filter((x) => norm(x.building) === norm(b.name) || similar(x.building, b.name) >= 0.8)) {
      const onPage = b.variations.find((v) => sameItem(v, { text: reg.text, ref: reg.ref }));
      if (!onPage) {
        if (reg.status && /pending pricing|pending submission|pending price/i.test(reg.status) && !findPm({ ref: reg.ref, text: reg.text, status: reg.status, page: null }, vars))
          out.push({ key: `reg|${norm(b.name)}|${norm(reg.text).slice(0, 40)}`, building: b.name, area: 'Variations', kind: 'not_in_pm', says: `The register at the back lists "${reg.text}" (${reg.status}). It is not on the ${b.name} page.`, pm: 'Not in Variations & Requests.', action: 'create_request', itemIds: [], page: null });
        continue;
      }
      if (onPage.status && reg.status && ((APPROVED.test(onPage.status) || ONGOING.test(onPage.status)) && PENDING_FUNDING.test(reg.status) && !APPROVED.test(reg.status))) {
        const hit = findPm(onPage, vars);
        out.push({ key: `contra|${norm(b.name)}|${refKey(onPage.ref ?? onPage.text) ?? norm(onPage.text).slice(0, 30)}`, building: b.name, area: 'Variations', kind: 'contradicts', says: `${label(onPage)} — page says "${onPage.status}"; the register at the back says "${reg.status}"${reg.amount_vep !== null ? ` — FJD ${reg.amount_vep.toLocaleString('en-GB', { minimumFractionDigits: 2 })} VEP` : ''}.`, pm: hit ? `${itemLabel(hit)} — ${hit.client_approved_at ? 'client approved' : 'not client approved'}` : 'Not in PM C3', action: 'raise_pbs', itemIds: hit ? [hit.id] : [], page: onPage.page });
      }
    }
    // RFIs and open issues with an RFI number
    for (const x of [...b.rfis, ...b.open_issues.filter((o) => /^rfi/.test(refKey(o.ref ?? o.text) ?? ''))]) {
      const hit = findPm(x, rfis);
      out.push(hit ? { key: `ok|${hit.id}`, building: b.name, area: 'RFIs', kind: 'matched', says: label(x), pm: `${itemLabel(hit)} — ${hit.status ?? 'open'}`, action: null, itemIds: [hit.id], page: x.page } : { key: `miss|rfi|${refKey(x.ref ?? x.text) ?? norm(x.text).slice(0, 30)}`, building: b.name, area: 'RFIs', kind: 'not_in_pm', says: label(x), pm: 'Not in RFIs & site instructions.', action: 'create_request', itemIds: [], page: x.page });
    }
    // inspections (no inspections register yet → a follow-up / note)
    for (const i of b.inspections)
      out.push({ key: `insp|${norm(b.name)}|${i.date ?? norm(i.text).slice(0, 30)}`, building: b.name, area: 'Inspections', kind: 'not_in_pm', says: `${i.text}${i.date ? ` (${i.date})` : ''}`, pm: 'Not booked in PM C3.', action: 'follow_up', itemIds: [], page: null });
    // claims: retention mentioned but no retention claim on the building
    if (b.claim && /retention/i.test(b.claim) && !claims.some((c) => inB(c) && /retention/i.test(`${c.title ?? ''} ${c.ref_no ?? ''}`)))
      out.push({ key: `ret|${norm(b.name)}`, building: b.name, area: 'Claims', kind: 'not_in_pm', says: b.claim, pm: 'No retention claim in PM C3 for this building.', action: 'follow_up', itemIds: [], page: null });
  }
  // de-duplicate by key (the same PM C3 item matched twice)
  const seen = new Set<string>();
  return out.filter((f) => (seen.has(f.key) ? false : (seen.add(f.key), true)));
}

/** Activities from the latest weekly report's look-ahead for one building — the daily log's tick list. */
export function lookaheadFor(r: WeeklyRead | null, buildingName: string): string[] {
  const b = r ? findB(r, buildingName) ?? r.buildings.find((x) => similar(x.name, buildingName) >= 0.5) ?? null : null;
  if (!b) return [];
  return [...new Set(b.lookahead.filter((l) => l.status !== 'complete').map((l) => (l.area ? `${l.area} — ${l.text}` : l.text)))].slice(0, 20);
}

export const WEEK_STATUS: Record<string, { label: string; tone: string }> = {
  received: { label: 'Received', tone: 'mute' },
  reading: { label: 'SWI AI reading…', tone: 'ai' },
  read: { label: 'Read — to review', tone: 'warn' },
  failed: { label: 'Read failed', tone: 'bad' },
  in_review: { label: 'In review', tone: 'warn' },
  reviewed: { label: 'Reviewed', tone: 'ok' },
};
